import {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";
import { getMe } from "../api/services";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dob?: string;
  location?: { address: string };
  /**
   * Ops-controlled overrides. When true, the corresponding SDA step's
   * "Next" button is enabled even without the underlying action being
   * completed. Set manually on the user row by ops — no UI to toggle.
   */
  skipBankRequirement?: boolean;
  skipIdentityVerificationRequirement?: boolean;
  /**
   * True when the tenant has an active bank payment method without an ACH
   * mandate. Drives the app-wide gate (BankMandateRoute) forcing them to
   * re-authorize or replace it before using the rest of the app.
   */
  hasBankWithoutMandate?: boolean;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  loginSent: boolean;
  pendingIdentifier: string | null;
  setLoginSent: (sent: boolean) => void;
  setPendingIdentifier: (id: string | null) => void;
  setToken: (token: string) => void;
  refreshUser: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingIdentifier, setPendingIdentifier] = useState<string | null>(
    null
  );
  const [loginSent, setLoginSent] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    getMe()
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("token");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const setToken = (token: string) => {
    localStorage.setItem("token", token);
    setLoading(true);
    getMe()
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("token");
        setUser(null);
      })
      .finally(() => setLoading(false));
  };

  const refreshUser = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      return;
    }
    setLoading(true);
    getMe()
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setLoginSent(false);
    setPendingIdentifier(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        loginSent,
        pendingIdentifier,
        setLoginSent,
        setPendingIdentifier,
        setToken,
        refreshUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
